import React, { useEffect, useRef, useState } from 'react';
import { http, ConfigContext } from '../../../../base-ui/lib.js';
import { Box, Button, Paper, Typography, useTheme } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import SendIcon from '@mui/icons-material/Send';
import { useNavigate } from 'react-router-dom';
import Spinner from '../../../../globalComponents/spinner.js';
import SpinnerSize from '../../../../enums/spinnerSize.js';
import { toastError, toastSuccess } from '../../../../Toaster/Toast.tsx';
import { useQuotationsCount } from '../../../../contexts/QuotationsCountContext';
import { useQuoteState } from 'src/stateProviders/QuoteStateProvider.js';
import { useApi } from '../../../../hooks/useApi.ts';
import QuoteStatus from 'src/enums/quoteStatus.js';

export function ProposalPreview() {
	const navigate = useNavigate();
	const { config }: any = React.useContext(ConfigContext);
	const [iframeUrl, setIframeUrl] = useState<string | null>(null);
	const iframeRef = useRef<HTMLIFrameElement>(null);
	const theme = useTheme();
	const quoteState = useQuoteState();
	const api = useApi();
	const quoteId = window.location.pathname.split('/')[2];
	const { refreshCounts } = useQuotationsCount();
	const status = quoteState.currentQuote?.status;
	const isDisabled =
		status &&
		[QuoteStatus.PROPOSALS_SENT, QuoteStatus.PROPOSALS_SIGNED].includes(status);

	const [isSending, setIsSending] = useState(false);

	const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

	const [isMobile, setIsMobile] = useState(() => {
		return (
			isSafari ||
			/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
				navigator.userAgent
			) ||
			(('ontouchstart' in window || navigator.maxTouchPoints > 0) &&
				window.innerWidth <= 1024)
		);
	});

	useEffect(() => {
		const handleResize = () => {
			const mobileDetection =
				isSafari ||
				/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
					navigator.userAgent
				) ||
				(('ontouchstart' in window || navigator.maxTouchPoints > 0) &&
					window.innerWidth <= 1024);
			setIsMobile(mobileDetection);
		};

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, [isSafari]);

	const openPdfInNewTab = () => {
		if (iframeUrl) {
			window.open(iframeUrl, '_blank');
		}
	};

	async function handleDownload() {
		try {
			const response = await http.post(
				{ quoteId: quoteId },
				`${config.uri.base}/esign/preview`,
				{
					responseType: 'arraybuffer',
				}
			);

			const contentType = 'application/pdf';
			const blob = new Blob([response], { type: contentType });
			const url = URL.createObjectURL(blob);
			setIframeUrl(url);
		} catch (error) {
			console.error('Error downloading document:', error);
		}
	}

	useEffect(() => {
		handleDownload();
		return () => {
			if (iframeUrl) URL.revokeObjectURL(iframeUrl);
		};
	}, []);

	const sendProposal = async () => {
		if (isSending) {
			return;
		}

		try {
			setIsSending(true);

			const response = await http.post(
				{ quoteId: quoteId },
				`${config.uri.base}/esign/sendProposal`
			);

			if (response.errored) {
				toastError({
					title: 'Error',
					description: `Could not send proposal: ${response.error.message}`,
				});
			} else {
				await refreshCounts();

				const hasSignInPerson = response.signers.some((signer) =>
					signer.contactMethod.find((cm) => cm.type == 'embed')
				);

				if (hasSignInPerson) {
					const signer = response.signers.filter((signer) =>
						signer.contactMethod.find((cm) => cm.type == 'embed')
					)[0];

					const embededContactMethod = signer.contactMethod.find(
						(cm) => cm.type == 'embed'
					);

					setTimeout(() => {
						navigate(
							`/signInPerson/${response.processId}/${embededContactMethod.signerId}`
						);
					}, 100);
				} else {
					toastSuccess({
						title: 'Success',
						description: 'Proposal sent successfully',
					});
					setTimeout(() => {
						navigate('/quotations');
					}, 100);
				}
			}
		} catch (error) {
			toastError({
				title: 'Error',
				description: 'An error occurred while sending the proposal',
			});
		} finally {
			setIsSending(false);
		}
	};

	return (
		<div>
			<Box>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'row',
						justifyContent: 'space-between',
						px: 0,
						py: 1,
						m: 0,
						alignItems: 'center',
					}}
				>
					<Typography variant="h5" gutterBottom>
						Preview Proposal
					</Typography>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
						<Button
							sx={{
								border: `1px solid ${theme.palette.primary[500]}`,
								borderRadius: theme.shape.borderRadius,
							}}
							onClick={() => {
								navigate(`/quotations/${quoteId}/pricing`);
							}}
						>
							Back
						</Button>
						<Button
							sx={{
								backgroundColor: theme.palette.primary.main,
								color: theme.palette.common.white,
								borderRadius: theme.shape.borderRadius,
							}}
							endIcon={
								isSending ? (
									<Spinner size={SpinnerSize.SMALL} />
								) : (
									<SendIcon sx={{ color: theme.palette.common.white }} />
								)
							}
							onClick={sendProposal}
							disabled={isSending}
						>
							Generate & Send Proposal
						</Button>
					</Box>
				</Box>

				<Paper
					elevation={3}
					sx={{ bgcolor: theme.palette.gray, borderRadius: 2, p: 2 }}
				>
					<Typography color="white" mb={2}>
						<InfoIcon color="white" sx={{ verticalAlign: 'middle', mr: 1 }} />
						Once you send this proposal, it cannot be edited without resending.
					</Typography>

					{!iframeUrl && (
						<Box
							sx={{
								width: '100%',
								height: '100%',
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								marginY: '300px',
							}}
						>
							<Spinner size={SpinnerSize.LARGE} />
						</Box>
					)}

					{iframeUrl && (
						<>
							{isMobile ? (
								<Box
									sx={{
										width: '100%',
										height: '400px',
										display: 'flex',
										flexDirection: 'column',
										justifyContent: 'center',
										alignItems: 'center',
										bgcolor: 'white',
										borderRadius: 1,
										gap: 2,
									}}
								>
									<Typography variant="h6" color="text.primary">
										PDF Preview
									</Typography>
									<Typography
										variant="body2"
										color="text.secondary"
										textAlign="center"
									>
										{isSafari
											? 'To Preview Proposal please click link below'
											: 'To Preview Proposal please click link below'}
										<br />
									</Typography>
									<Button
										variant="contained"
										onClick={openPdfInNewTab}
										sx={{
											backgroundColor: theme.palette.primary.main,
											color: theme.palette.common.white,
										}}
									>
										Open PDF in New Tab
									</Button>
								</Box>
							) : (
								<Box>
									<Box
										sx={{ mb: 1, display: 'flex', justifyContent: 'flex-end' }}
									>
										<Button
											variant="outlined"
											onClick={openPdfInNewTab}
											size="small"
											sx={{
												color: 'white',
												borderColor: 'white',
												'&:hover': {
													borderColor: 'white',
													backgroundColor: 'rgba(255, 255, 255, 0.1)',
												},
											}}
										>
											Open in New Tab
										</Button>
									</Box>
									<iframe
										ref={iframeRef}
										src={iframeUrl ? `${iframeUrl}#toolbar=1&view=FitH` : ''}
										width="100%"
										height="1080"
										title="Document Preview"
										style={{ border: 'none' }}
									/>
								</Box>
							)}
						</>
					)}
				</Paper>
			</Box>
		</div>
	);
}
